/**
 * Booking time validation shared by the API and the public site so the two can
 * never disagree on what counts as a "past" booking.
 *
 * All comparisons are made against the Morocco wall clock (`Africa/Casablanca`).
 * Using the IANA zone — rather than a hardcoded UTC+1 offset — means the
 * Ramadan UTC+0 shift is handled automatically by the runtime's tz data.
 */

const MOROCCO_TZ = "Africa/Casablanca";
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export interface MoroccoNow {
  /** Wall-clock date in Morocco as `YYYY-MM-DD`. */
  date: string;
  /** Wall-clock time in Morocco as 24h `HH:MM`. */
  time: string;
}

/**
 * Built once and reused. Constructing an Intl.DateTimeFormat loads ICU locale
 * and zone data, which on Hermes costs far more than formatting with it, and
 * the booking sheet asks for "now" on every render and for every time slot.
 * The formatter holds no clock state: the zone's offset (Ramadan's UTC+0
 * included) is still looked up per instant in `formatToParts`.
 * Lazy so importing this module costs nothing on a surface that never asks.
 */
let moroccoFormatter: Intl.DateTimeFormat | null = null;
function moroccoParts(now: Date): Intl.DateTimeFormatPart[] {
  if (!moroccoFormatter) {
    moroccoFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: MOROCCO_TZ,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  return moroccoFormatter.formatToParts(now);
}

/** Current Morocco wall-clock date/time for a given instant (defaults to now). */
export function getMoroccoNow(now: Date = new Date()): MoroccoNow {
  const parts = moroccoParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  let hour = get("hour");
  // Some ICU builds emit "24" for midnight under hour12:false — normalize to 00.
  if (hour === "24") hour = "00";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${hour}:${get("minute")}`,
  };
}

/**
 * Midnight on Morocco's current calendar day, expressed in the HOST zone's
 * calendar parts — so `getFullYear/getMonth/getDate` and `toLocaleDateString`
 * all read back the Morocco day whatever clock the machine is on.
 *
 * Every booking date strip used to start from a plain `new Date()`. That is the
 * host clock: the Cloudflare worker renders in UTC, the browser hydrates in the
 * visitor's zone, and the moment those two sit on different calendar days the
 * whole strip shifts by a day and React aborts hydration. Measured on
 * production 2026-09-12 at 00:56 Morocco (23:56 UTC): the server sent
 * "ven. 11 sept." and the browser drew "sam. 12 sept.", React error #418. The
 * window is not a Moroccan curiosity — against a UTC server it is roughly
 * 19:00 to midnight every night for a visitor on the US east coast.
 *
 * Morocco is the right anchor rather than the visitor's own midnight because
 * it is the clock the booking rules already use: `isBookingPast` judges every
 * slot against `getMoroccoNow`, so a strip built from the visitor's day could
 * offer a "today" the server then rejects as past.
 */
export function moroccoStartOfToday(now: Date = new Date()): Date {
  const { date } = getMoroccoNow(now);
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

const WEEKDAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;

/**
 * Whether a venue takes bookings on `date`. `openDays` lists weekday names
 * ("friday", "saturday"); null or empty means every day. A malformed date is
 * treated as open so shape validation stays the caller's job.
 */
export function isDayOpen(date: string, openDays: readonly string[] | null | undefined): boolean {
  if (!openDays || openDays.length === 0) return true;
  if (!DATE_RE.test(date)) return true;
  const weekday = WEEKDAY_NAMES[new Date(`${date}T00:00:00Z`).getUTCDay()]!;
  return openDays.includes(weekday);
}

/** Add whole days to a `YYYY-MM-DD` string. Malformed input is returned as-is. */
function shiftIsoDate(date: string, days: number): string {
  if (days === 0 || !DATE_RE.test(date)) return date;
  const shifted = new Date(`${date}T00:00:00Z`);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}

/**
 * How many days past the booked date each slot actually falls on.
 *
 * A venue open 19:00–01:00 stores its seatings in service order — 19:00 … 23:00,
 * 00:00, 01:00 — and the last two belong to the *next* calendar day. Without
 * this, a plain `"00:00" <= now` comparison reads midnight as twelve hours
 * behind rather than a few hours ahead, so every after-midnight seating
 * disappeared from same-day bookings for the whole evening.
 *
 * The wrap is found by position, not by clock value: once the list steps
 * backwards it has crossed midnight, and everything from there on is +1. A list
 * that steps backwards twice is not in service order (sorted, or simply junk),
 * so every slot is left on the booked date rather than guessed at.
 */
export function serviceDayOffsets(times: readonly string[]): number[] {
  const offsets = new Array<number>(times.length).fill(0);
  let previous: string | null = null;
  let offset = 0;
  for (let i = 0; i < times.length; i += 1) {
    const time = times[i];
    if (!time || !TIME_RE.test(time)) continue;
    if (previous !== null && time < previous) {
      if (offset === 1) return new Array<number>(times.length).fill(0);
      offset = 1;
    }
    offsets[i] = offset;
    previous = time;
  }
  return offsets;
}

/**
 * A time at or after this is the tail of the previous evening's service, not
 * the start of its own morning. Kitchens close in the small hours; nothing
 * opens at 04:00 and also runs to midnight.
 */
const SMALL_HOURS_END_MIN = 5 * 60;
/** A time at or after this is "evening" for the purpose of finding a wrap. */
const EVENING_START_MIN = 18 * 60;

/** `"19:00"` → 1140. Malformed input sorts last. */
function timeToMinutes(time: string): number {
  const [h, m] = time.split(":");
  return Number(h) * 60 + Number(m);
}

/**
 * Put a set of `HH:MM` times into SERVICE order — the order every other helper
 * here reads, where a venue running past midnight ends its list with the small
 * hours rather than starting with them.
 *
 * A plain `.sort()` is right for a daytime venue and wrong for a late-night
 * one: Folk serves 19:00–01:00 and sorted as strings its picker read
 * "00:00, 01:00, 19:00, 20:00 …". That is not only odd to read — it is the
 * wrong answer, because `serviceDayOffsets` finds the midnight wrap by
 * position, so a list that opens at midnight dates those two seatings to the
 * morning of the booked day instead of the night after it.
 *
 * The wrap only applies when the set actually spans one: small hours AND an
 * evening. A sunrise balloon at 04:00 with a 09:00 sibling is a morning, and
 * is left in clock order.
 */
export function sortServiceTimes(times: readonly string[]): string[] {
  const clean = [...new Set(times.filter((t) => TIME_RE.test(t)))];
  const minutes = clean.map(timeToMinutes);
  const wraps =
    minutes.some((m) => m < SMALL_HOURS_END_MIN) &&
    minutes.some((m) => m >= EVENING_START_MIN);
  const key = (time: string) => {
    const m = timeToMinutes(time);
    return wraps && m < SMALL_HOURS_END_MIN ? m + 24 * 60 : m;
  };
  return clean.sort((a, b) => key(a) - key(b));
}

export interface BookingMoment {
  /** Booking date as `YYYY-MM-DD`. */
  date: string | null | undefined;
  /** Optional start time as 24h `HH:MM`. Absent for date-only bookings (stays). */
  time?: string | null;
  /**
   * The venue's whole slot list, in service order. Only meaningful for a service
   * that runs past midnight: it is what tells us a 00:00 seating booked for
   * Wednesday is Thursday morning, not Wednesday morning. Omit it and every time
   * is read as falling on `date` itself.
   */
  serviceTimes?: readonly string[] | null;
}

/**
 * Is this booking's start moment at or before "now" in Morocco?
 *
 * - With a time: past when the date is earlier than today, or the date is today
 *   and the time is at/before the current minute (the slot is gone at `HH:MM` sharp).
 * - Date only (no time): past only when the date is before today — a same-day
 *   date-only booking stays valid all day.
 * - With `serviceTimes`: a time that sits after the list's midnight wrap is
 *   measured against the following day, so a late-night venue keeps offering
 *   00:00 and 01:00 through the evening they belong to.
 *
 * Returns `false` when the date is missing or malformed; shape validation is the
 * caller's job, this helper only answers the past/future question.
 */
export function isBookingPast(
  booking: BookingMoment,
  now: Date = new Date(),
): boolean {
  const { date } = booking;
  if (!date || !DATE_RE.test(date)) return false;
  return isPastAt(booking, getMoroccoNow(now));
}

/**
 * `isBookingPast` against a Morocco wall clock already read, so a caller
 * judging a whole list of slots reads the clock once rather than per slot.
 */
function isPastAt(booking: BookingMoment, moroccoNow: MoroccoNow): boolean {
  const { date } = booking;
  if (!date || !DATE_RE.test(date)) return false;
  const time = booking.time && TIME_RE.test(booking.time) ? booking.time : null;

  if (time) {
    const effectiveDate = shiftIsoDate(
      date,
      serviceDayOffset(booking.serviceTimes, time),
    );
    if (effectiveDate < moroccoNow.date) return true;
    if (effectiveDate === moroccoNow.date) return time <= moroccoNow.time;
    return false;
  }
  return date < moroccoNow.date;
}

/**
 * Has a night that runs past midnight finished?
 *
 * A nightclub sells the NIGHT, not a departure time. Leone opens at 00:00 and
 * closes at 05:00, so `isBookingPast` — which asks "has the start time gone?"
 * — calls the night dead one minute after it begins, and a guest deciding at
 * 02:50 to go out is refused for a club that is open and full. That is the
 * moment most club bookings are actually made, and the refusal landed on the
 * final Confirm tap after the guest had filled in everything (owner, on their
 * own phone, 2026-09-19).
 *
 * The night is over when its CLOSING time has passed. `open`/`close` are the
 * venue's own hours; a close at or before the open means it runs into the next
 * morning, so the deadline moves to the following day.
 *
 * Malformed hours fall back to `isBookingPast`, which is the stricter answer:
 * a venue with no usable hours should not gain an open-ended grace period.
 */
export function isNightOver(
  night: { date: string | null | undefined; open?: string | null; close?: string | null },
  now: Date = new Date(),
): boolean {
  const { date, open, close } = night;
  if (!date || !DATE_RE.test(date)) return false;
  if (!open || !close || !TIME_RE.test(open) || !TIME_RE.test(close)) {
    return isBookingPast({ date, time: open ?? null }, now);
  }

  // A close at or before the open runs into the next morning: 00:00–05:00 ends
  // on the booked day, 23:00–05:00 ends the day after.
  const endsNextDay = timeToMinutes(close) <= timeToMinutes(open) && timeToMinutes(open) > 0;
  const endDate = shiftIsoDate(date, endsNextDay ? 1 : 0);

  const moroccoNow = getMoroccoNow(now);
  if (endDate < moroccoNow.date) return true;
  if (endDate > moroccoNow.date) return false;
  return close <= moroccoNow.time;
}

/** The day offset a single `time` carries within its own service list. */
function serviceDayOffset(
  serviceTimes: readonly string[] | null | undefined,
  time: string,
): number {
  if (!Array.isArray(serviceTimes) || serviceTimes.length === 0) return 0;
  const index = serviceTimes.indexOf(time);
  if (index < 0) return 0;
  return serviceDayOffsets(serviceTimes)[index] ?? 0;
}

/**
 * A table at 00:00 is the midnight that ENDS the evening the guest picked, and
 * it is stored and shown on that evening's date: "Fri 9 Oct, 00:00". Filed on
 * the morning after, it went out as "Sat 10 Oct à 00:00", which guests and
 * venues both read as Saturday night, a day late (Milla Supper Club,
 * 2026-10-09; owner ruled the 00:00 slot stays on the day picked).
 */
const MIDNIGHT = "00:00";

/**
 * The calendar date a booking is stored on, given the night it was booked for.
 *
 * `isBookingPast` and `filterFutureTimes` already read a late-night list this
 * way, so the guest is correctly offered tonight's 01:00 seating. What is
 * stored has to agree: the picker speaks in nights, and a 01:00 table chosen
 * under Friday is served on Saturday. Save Friday and the venue expects that
 * guest a day early, and the admin list shows a time the restaurant was shut.
 *
 * 00:00 is the exception: it keeps the night's own date (see `MIDNIGHT`), and
 * `bookingStartDate` turns it back into the real day when one is needed.
 *
 * Any time before the list's midnight wrap, and any venue that does not trade
 * past midnight, is returned unchanged.
 */
export function resolveServiceDate(
  date: string,
  time: string | null | undefined,
  serviceTimes: readonly string[] | null | undefined,
): string {
  if (!time || !TIME_RE.test(time) || time === MIDNIGHT) return date;
  return shiftIsoDate(date, serviceDayOffset(serviceTimes, time));
}

/**
 * The calendar day a STORED booking starts on, for anything that turns the row
 * into a moment: countdowns, calendar invites, "upcoming" sorting.
 *
 * A stored 00:00 table sits on the evening it ends (`resolveServiceDate`), so
 * its moment is the next day's 00:00. A nightclub is the exception: it sells
 * the night from 00:00 of its own date (`isNightOver`), so it is left alone.
 */
export function bookingStartDate(
  date: string,
  time: string | null | undefined,
  opts: { nightclub?: boolean } = {},
): string {
  if (opts.nightclub || time?.slice(0, 5) !== MIDNIGHT) return date;
  return shiftIsoDate(date, 1);
}

/**
 * Keep only the `HH:MM` times that have not yet passed on `date` (Morocco time),
 * preserving order. Used by the booking UIs so a date of "today" never offers an
 * hour that is already gone (and never auto-selects one).
 *
 * The list is its own midnight context: seatings after the wrap are measured
 * against the next day, so a venue serving until 01:00 keeps offering its last
 * two slots all evening instead of losing them the moment 01:00 has passed.
 */
export function filterFutureTimes(
  times: readonly string[],
  date: string,
  now: Date = new Date(),
): string[] {
  const offsets = serviceDayOffsets(times);
  // One clock read for the whole list: every slot is judged against the same
  // minute, which is also what keeps the answer consistent across the list.
  const moroccoNow = getMoroccoNow(now);
  return times.filter(
    (time, i) =>
      !isPastAt({ date: shiftIsoDate(date, offsets[i] ?? 0), time }, moroccoNow),
  );
}

/**
 * Seatings offered by a restaurant that has not had its own service times
 * entered yet. Half-hourly from lunch to late dinner. Set `time_slots` on the
 * venue to replace this with the seatings it actually honours.
 */
export const DEFAULT_RESTAURANT_TIME_SLOTS: readonly string[] = [
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
  "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00",
];

/**
 * How far ahead a restaurant table can be booked, in calendar months. Venues
 * do not plan seatings that far out, so a request beyond it cannot be confirmed
 * with the provider and is refused up front.
 */
export const RESTAURANT_BOOKING_WINDOW_MONTHS = 2;

/**
 * The last date (`YYYY-MM-DD`, Morocco calendar) a restaurant can be booked for.
 * Months are added on the calendar, clamped to the month's last day, so
 * 31 December allows up to 28 (or 29) February.
 */
export function lastRestaurantBookingDate(now: Date = new Date()): string {
  const [y, m, d] = getMoroccoNow(now).date.split("-").map(Number);
  const target = new Date(Date.UTC(y, m - 1 + RESTAURANT_BOOKING_WINDOW_MONTHS, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}

/** Is `date` further out than a restaurant can be booked? */
export function isBeyondRestaurantWindow(
  date: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!date || !DATE_RE.test(date)) return false;
  return date > lastRestaurantBookingDate(now);
}

/* -------------------------------------------------------------------------- */
/* Ride notice                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * How long before its pick-up a ride has to be booked, in minutes. The owner's
 * rule (2026-09-30): "Transport must be arranged at least 2 hours before." At
 * 19:00 a transfer to a 20:00 table, or a 20:00 table with the pick-up option
 * ticked, cannot be booked: nobody can get a driver there in time.
 *
 * Applies to every ride a guest books for themselves: a transfer, and the
 * pick-up add-on on an activity or table. Our team can still arrange a
 * last-minute ride by hand in admin; that path does not check it.
 */
export const RIDE_NOTICE_MINUTES = 120;

export interface RideCutoff {
  /** The Morocco calendar day the earliest bookable pick-up falls on. */
  date: string;
  /** Minutes past midnight on `date`: a pick-up earlier than this is too soon. */
  minutes: number;
}

/**
 * The earliest pick-up a guest can still book, on Morocco's clock: now plus
 * `RIDE_NOTICE_MINUTES`. Late in the evening it rolls into tomorrow (at 23:00
 * the first ride is tomorrow at 01:00).
 */
export function rideCutoff(now: Date = new Date()): RideCutoff {
  const { date, time } = getMoroccoNow(now);
  const minutes = timeToMinutes(time) + RIDE_NOTICE_MINUTES;
  const days = Math.floor(minutes / (24 * 60));
  return { date: shiftIsoDate(date, days), minutes: minutes - days * 24 * 60 };
}

/**
 * On `date`, the first pick-up minute (past midnight) that can still be
 * booked: `0` when the whole day is open, `24 * 60` when none of it is. For a
 * time picker, a slot is too soon when its minutes are below this.
 */
export function earliestRideMinutes(date: string, now: Date = new Date()): number {
  if (!DATE_RE.test(date)) return 0;
  const cutoff = rideCutoff(now);
  if (date < cutoff.date) return 24 * 60;
  if (date > cutoff.date) return 0;
  return cutoff.minutes;
}

/**
 * Is this ride's pick-up less than `RIDE_NOTICE_MINUTES` away (Morocco time)?
 *
 * - With a time: the pick-up is `date` at `time`, read in service order when
 *   `serviceTimes` is given (a 01:00 seating under Friday is Saturday's).
 * - Date only: the pick-up time is not known yet, so the ride is too soon only
 *   once no time left on that day would still be far enough away.
 *
 * Returns `false` for a missing or malformed date; shape validation is the
 * caller's job.
 */
export function isRideTooSoon(ride: BookingMoment, now: Date = new Date()): boolean {
  const { date } = ride;
  if (!date || !DATE_RE.test(date)) return false;
  const cutoff = rideCutoff(now);
  const time = ride.time && TIME_RE.test(ride.time) ? ride.time : null;
  if (!time) return date < cutoff.date;
  const effectiveDate = shiftIsoDate(date, serviceDayOffset(ride.serviceTimes, time));
  if (effectiveDate < cutoff.date) return true;
  if (effectiveDate > cutoff.date) return false;
  return timeToMinutes(time) < cutoff.minutes;
}

/* -------------------------------------------------------------------------- */
/* An activity's own notice                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The earliest moment an activity with `minNoticeHours` of notice can still be
 * booked for, on Morocco's clock: now plus that many hours. The babouche
 * workshop asks for 48 hours (round 18, item 167): its page said so, yet the
 * booking offered today at 10:00, and a guest who took it got a WhatsApp
 * "sorry" instead of a pair of slippers.
 *
 * `null`, `0` or a negative value means no notice beyond the usual "not in the
 * past"; the cutoff is then now itself.
 */
export function noticeCutoff(minNoticeHours: number | null | undefined, now: Date = new Date()): RideCutoff {
  const { date, time } = getMoroccoNow(now);
  const extra = minNoticeHours && minNoticeHours > 0 ? Math.round(minNoticeHours * 60) : 0;
  const minutes = timeToMinutes(time) + extra;
  const days = Math.floor(minutes / (24 * 60));
  return { date: shiftIsoDate(date, days), minutes: minutes - days * 24 * 60 };
}

/** True when every time on `date` falls inside the notice: the whole day is too soon to book. */
export function isDayTooSoon(
  date: string,
  minNoticeHours: number | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!minNoticeHours || minNoticeHours <= 0 || !DATE_RE.test(date)) return false;
  return date < noticeCutoff(minNoticeHours, now).date;
}

/**
 * Is this booking inside the activity's notice? With a time, the moment is
 * `date` at `time` (read in service order when `serviceTimes` is given); with
 * a date only, it is too soon once the whole day is. No notice set: `false`.
 */
export function isWithinNotice(
  moment: BookingMoment,
  minNoticeHours: number | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!minNoticeHours || minNoticeHours <= 0) return false;
  const { date } = moment;
  if (!date || !DATE_RE.test(date)) return false;
  const cutoff = noticeCutoff(minNoticeHours, now);
  const time = moment.time && TIME_RE.test(moment.time) ? moment.time : null;
  if (!time) return date < cutoff.date;
  const effectiveDate = shiftIsoDate(date, serviceDayOffset(moment.serviceTimes, time));
  if (effectiveDate < cutoff.date) return true;
  if (effectiveDate > cutoff.date) return false;
  return timeToMinutes(time) < cutoff.minutes;
}

/** `times` on `date` that are outside the notice, in order. No notice set: `times` unchanged. */
export function filterNoticeTimes(
  times: readonly string[],
  date: string,
  minNoticeHours: number | null | undefined,
  now: Date = new Date(),
): string[] {
  if (!minNoticeHours || minNoticeHours <= 0) return [...times];
  return times.filter((time) => !isWithinNotice({ date, time, serviceTimes: times }, minNoticeHours, now));
}
